import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/About.css';
import {
  FaBuilding,
  FaBullseye,
  FaEye,
  FaRocket,
  FaGem,
  FaShieldAlt,
  FaComments,
  FaMoneyBillWave,
  FaTrophy,
  FaHandshake,
  FaBolt,
  FaFistRaised,
  FaGlobe,
  FaHeart,
  FaMagic,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt
} from 'react-icons/fa';

const About = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="About Us - BookMyCamera | Professional Photography Services"
        description="Learn about BookMyCamera - your trusted partner for professional photography, videography services, and camera equipment rental in Bangalore."
        keywords="about bookmycamera, photography company bangalore, videography services bangalore, camera rental company"
      />
    <div className="about-container">
      <div className="about-hero">
        <h1>About Us</h1>
        <p>We're on a mission to revolutionize the way businesses operate in the digital age</p>
      </div>

      <div className="about-content">
        <div className="about-text">
          <h2>Our Story</h2>
          <p>Founded in 2020, Book My Camera began with a passion for photography and innovation. We’ve been helping people and businesses capture moments, create memories, and transform their operations with smart, tech-driven solutions.</p>
          <p>Our team combines decades of experience in software development, business strategy, customer success, and creative photography. Together, we work to make photography easier, more accessible, and more enjoyable for everyone.</p>
          <p>Whether it’s a wedding, travel adventure, or professional shoot, Book My Camera connects you with skilled photographers and the right equipment to bring your vision to life.</p>
          <p>We believe in building products and experiences that don’t just solve problems — they inspire creativity and delight users every step of the way.</p>
        </div>
        <div className="about-image">
          <FaBuilding />
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="mission-vision-section">
        <div className="mission-card">
          <div className="mv-icon"><FaBullseye /></div>
          <h3>Our Mission</h3>
          <p>To empower businesses of all sizes with innovative technology solutions that drive growth, efficiency, and success in the digital era.</p>
        </div>
        <div className="vision-card">
          <div className="mv-icon"><FaEye /></div>
          <h3>Our Vision</h3>
          <p>To be the world's most trusted technology partner, transforming industries and creating lasting value for our customers and communities.</p>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="why-choose-section">
        <h2>Why Choose Us?</h2>
        <div className="why-grid">
          <div className="why-card">
            <div className="why-icon"><FaGem /></div>
            <h4>Premium Quality</h4>
            <p>Top-notch products that exceed expectations</p>
          </div>
          <div className="why-card">
            <div className="why-icon"><FaShieldAlt /></div>
            <h4>Secure & Reliable</h4>
            <p>Industry-standard security and reliability</p>
          </div>
          <div className="why-card">
            <div className="why-icon"><FaComments /></div>
            <h4>24/7 Support</h4>
            <p>Always here to help when you need us</p>
          </div>
          <div className="why-card">
            <div className="why-icon"><FaMoneyBillWave /></div>
            <h4>Best Pricing</h4>
            <p>Competitive rates with maximum value</p>
          </div>
          <div className="why-card">
            <div className="why-icon"><FaTrophy /></div>
            <h4>Award Winning</h4>
            <p>Recognized for excellence in the industry</p>
          </div>
        </div>
      </div>

      <div className="values-section">
        <h2>Our Core Values</h2>
        <div className="values-grid">
          <div className="value-card">
            <div className="value-icon"><FaBullseye /></div>
            <h3>Innovation</h3>
            <p>We continuously push boundaries to create innovative solutions that drive real results.</p>
          </div>
          <div className="value-card">
            <div className="value-icon"><FaHandshake /></div>
            <h3>Integrity</h3>
            <p>We operate with transparency, honesty, and ethical practices in everything we do.</p>
          </div>
          <div className="value-card">
            <div className="value-icon"><FaBolt /></div>
            <h3>Excellence</h3>
            <p>We strive for excellence in every product, service, and interaction with our customers.</p>
          </div>
          <div className="value-card">
            <div className="value-icon"><FaFistRaised /></div>
            <h3>Empowerment</h3>
            <p>We empower our customers and team members to achieve their full potential.</p>
          </div>
        </div>
      </div>

      {/* Milestones Timeline */}
      <div className="milestones-section">
        <h2>Our Journey</h2>
        <p className="journey-subtitle">5 Years of Innovation, Growth, and Excellence</p>
        <div className="timeline">
          <div className="timeline-item" data-aos="fade-right">
            <div className="timeline-year-wrapper">
              <div className="timeline-dot"></div>
              <div className="timeline-year">2025</div>
            </div>
            <div className="timeline-content">
              <div className="timeline-icon"><FaMagic /></div>
              <h4>Innovation Continues</h4>
              <p>Leading the way with cutting-edge solutions and expanding horizons</p>
              <span className="timeline-badge">Future</span>
            </div>
          </div>
          <div className="timeline-item" data-aos="fade-left">
            <div className="timeline-year-wrapper">
              <div className="timeline-dot"></div>
              <div className="timeline-year">2024</div>
            </div>
            <div className="timeline-content">
              <div className="timeline-icon"><FaHeart /></div>
              <h4>10,000+ Happy Customers</h4>
              <p>Celebrated serving thousands of satisfied clients across the globe</p>
              <span className="timeline-badge">Milestone</span>
            </div>
          </div>
          <div className="timeline-item" data-aos="fade-right">
            <div className="timeline-year-wrapper">
              <div className="timeline-dot"></div>
              <div className="timeline-year">2023</div>
            </div>
            <div className="timeline-content">
              <div className="timeline-icon"><FaTrophy /></div>
              <h4>Award Recognition</h4>
              <p>Received "Best Innovation Award" in tech industry for outstanding achievements</p>
              <span className="timeline-badge">Excellence</span>
            </div>
          </div>
          <div className="timeline-item" data-aos="fade-left">
            <div className="timeline-year-wrapper">
              <div className="timeline-dot"></div>
              <div className="timeline-year">2022</div>
            </div>
            <div className="timeline-content">
              <div className="timeline-icon"><FaGlobe /></div>
              <h4>Global Expansion</h4>
              <p>Extended services to 15+ countries worldwide, making a global impact</p>
              <span className="timeline-badge">Expansion</span>
            </div>
          </div>
          <div className="timeline-item" data-aos="fade-right">
            <div className="timeline-year-wrapper">
              <div className="timeline-dot"></div>
              <div className="timeline-year">2021</div>
            </div>
            <div className="timeline-content">
              <div className="timeline-icon"><FaBullseye /></div>
              <h4>First 1,000 Customers</h4>
              <p>Reached our first major milestone in customer satisfaction and trust</p>
              <span className="timeline-badge">Growth</span>
            </div>
          </div>
          <div className="timeline-item" data-aos="fade-left">
            <div className="timeline-year-wrapper">
              <div className="timeline-dot"></div>
              <div className="timeline-year">2020</div>
            </div>
            <div className="timeline-content">
              <div className="timeline-icon"><FaRocket /></div>
              <h4>Company Founded</h4>
              <p>Started with a vision to transform digital solutions and revolutionize the industry</p>
              <span className="timeline-badge">Foundation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Get in Touch Section */}
      <div className="get-in-touch-section">
        <div className="touch-content">
          <h2>Ready to Get Started?</h2>
          <p>Join thousands of satisfied customers and experience the difference. Our team is ready to help you achieve your goals.</p>
          <div className="touch-buttons">
            <button className="touch-btn primary" onClick={() => navigate('/contact')}>
              Contact Us
            </button>
            <button className="touch-btn secondary" onClick={() => navigate('/products')}>
              View Products
            </button>
          </div>
          <div className="touch-info">
            <div className="touch-info-item">
              <span className="touch-info-icon"><FaEnvelope /></span>
              <span>{process.env.REACT_APP_EMAIL || 'bookmycamera@gmail.com'}</span>
            </div>
            <div className="touch-info-item">
              <span className="touch-info-icon"><FaPhone /></span>
              <span>{process.env.REACT_APP_PHONE_NUMBER ? `+91 ${process.env.REACT_APP_PHONE_NUMBER}` : '+91 1234567890'}</span>
            </div>
            <div className="touch-info-item">
              <span className="touch-info-icon"><FaMapMarkerAlt /></span>
              <span>{process.env.REACT_APP_ADDRESS || '23, 12th Main Rd, 4th Block East, KV Layout, Jayanagar, Bengaluru, Karnataka 560011'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default About;
